/**
 * Plans the coming week's work blocks the first time you open Project Hub
 * after your planning time (Planner › Week ahead › Standing blocks › Hours &
 * defaults; Sunday 5 PM to start with).
 *
 * In the browser because that is where your sign-in is: the Worker runs the
 * whole thing with it, so no login has to be stored anywhere. The cost is
 * that nothing happens until you open the app — on any device, since this is
 * mounted in both shells, like the Todoist sync.
 *
 * Checks on load, on returning to the tab, and every ten minutes while open
 * (for a tab left open across the planning time). A failure waits half an
 * hour before trying again, so a broken setup does not retry on every focus.
 */

import { useCallback, useEffect, useRef } from "react";

import { useToast } from "@/context/ToastContext";
import { useRunBlocks, useWorkBlockConfig, useWorkBlockMutations, useWorkBlockSettings } from "@/hooks/useWorkBlocks";
import { appMode } from "@/lib/pocketbase";
import { toIsoLocal } from "@/lib/timeRollup";
import { autoRunDue, localStamp, weekLabel } from "@/lib/workBlocks";

const TRY_KEY = "ph.workblocks.autoTry";
const RETRY_AFTER_MS = 30 * 60_000;
const CHECK_EVERY_MS = 10 * 60_000;

export function useWorkBlockAutoRun(): void {
  const toast = useToast();
  const { data } = useWorkBlockSettings();
  const { data: config } = useWorkBlockConfig();
  // The mutate functions are stable across renders; the mutation objects are not.
  const { mutate: saveSettings } = useWorkBlockMutations().saveSettings;
  const { mutateAsync: runBlocks } = useRunBlocks();
  const busy = useRef(false);

  const check = useCallback(async () => {
    if (appMode !== "live" || busy.current || !data) return;
    const s = data.settings;
    if (!s.autoRun) return;
    const now = new Date();
    const today = toIsoLocal(now);
    const nowMin = now.getHours() * 60 + now.getMinutes();

    // The first time it is seen on, it starts counting from now: an earlier
    // planning time must not replan the week you are already in.
    if (!s.autoRunSince) {
      busy.current = true;
      saveSettings({ ...s, autoRunSince: localStamp(today, nowMin) }, { onSettled: () => { busy.current = false; } });
      return;
    }

    if (!config?.token || !config.scriptUrl || config.error) return;
    const week = autoRunDue(today, nowMin, s, data.lastRun?.weeks ?? []);
    if (!week) return;

    try {
      const last = JSON.parse(localStorage.getItem(TRY_KEY) || "null") as { week: string; at: number } | null;
      if (last?.week === week && Date.now() - last.at < RETRY_AFTER_MS) return;
      localStorage.setItem(TRY_KEY, JSON.stringify({ week, at: Date.now() }));
    } catch { /* private mode: no back-off, still at most one run at a time */ }

    busy.current = true;
    toast(`Placing work blocks for ${weekLabel(week)}…`);
    try {
      const r = await runBlocks({ weekStart: week, dryRun: false, source: "auto" });
      const { placed, missed } = r.counts;
      toast(`Work blocks for ${weekLabel(week)}: ${placed} on your calendar${missed ? `, ${missed} found no slot` : ""}`);
    } catch (err) {
      toast(`Automatic planning failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      busy.current = false;
    }
  }, [data, config, saveSettings, runBlocks, toast]);

  useEffect(() => { void check(); }, [check]);

  useEffect(() => {
    const onFocus = () => void check();
    window.addEventListener("focus", onFocus);
    const timer = window.setInterval(onFocus, CHECK_EVERY_MS);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.clearInterval(timer);
    };
  }, [check]);
}
